<template>
  <nav class="user-menu" :class="{ desktop: $isDesktop, en: localeIsEn }">
    <nuxt-link
      v-if="$isDesktop"
      class="dashboard-link"
      :to="$puLocalePath({ name: 'account-dashboard' })"
      ><translate class="menu-item-text">Home</translate></nuxt-link
    >

    <nuxt-link
      :to="$puLocalePath({ name: 'account-bookings' })"
      class="user-bookings"
    >
      <translate class="menu-item-text">Bookings</translate>
    </nuxt-link>

    <nuxt-link
      :to="$puLocalePath({ name: 'favourites' })"
      class="user-favourites"
      @click="appMenu.hideMenu"
    >
      <translate class="menu-item-text">Favourites</translate>
    </nuxt-link>

    <nuxt-link
      :to="$puLocalePath({ name: 'account-reviews' })"
      class="user-reviews"
      ><PendingReviewsBadge />
      <translate class="menu-item-text">Reviews</translate>
    </nuxt-link>

    <nuxt-link
      :to="$puLocalePath({ name: 'account-availability-alerts' })"
      class="user-messages"
    >
      <translate class="menu-item-text">Availability alerts</translate>
    </nuxt-link>

    <nuxt-link :to="$puLocalePath({ name: 'account' })" class="user-account">
      <translate class="menu-item-text">Account details</translate>
    </nuxt-link>

    <nuxt-link
      :to="$puLocalePath({ name: 'account-settings' })"
      class="user-settings"
    >
      <translate class="menu-item-text">Settings</translate>
    </nuxt-link>
  </nav>

  <a
    v-if="isManager"
    :href="`/${$i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''}supplier2/`"
    target="_blank"
    class="manager-portal"
    :class="{ desktop: $isDesktop }"
  >
    <translate class="menu-item-text">Manager portal</translate>
    <translate tag="p">
      View and manage your listing's allocation, bookings and reviews
    </translate>
  </a>
</template>

<script lang="ts">
import AuthMixin from '~/mixins/AuthMixin'
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'

@Component({
  mixins: [AuthMixin],
  setup: () => {
    return {
      appMenu: useAppMenuStore(),
    }
  },
})
class AccountSectionsMenu extends Vue {
  get localeIsEn() {
    return this.$i18n.locale.startsWith('en-')
  }
}

export default toNative(AccountSectionsMenu)
</script>

<style scoped>
.user-menu {
  list-style: none;
  margin: 0 1.4em 1.4em;
  padding: 0;
  width: auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(5.5em, 1fr));
  justify-content: center;
  gap: 1em;
}

.user-menu.manager {
  margin-bottom: 3em;
}

nav.user-menu {
  display: flex;
  flex-wrap: wrap;
  place-content: start;
  flex-direction: row;
  gap: 1em;
  margin: 1.4em;
}

.user-menu a {
  align-items: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 0.3em;
  box-shadow: var(--card-box-shadow);
  color: var(--colour-dark-blue);
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-end;
  max-width: calc((100% - 1em) / 2);
  min-height: 5em;
  min-width: 4em;
  overflow-wrap: break-word;
  position: relative;
  text-decoration: none;
  user-select: none;
  transition:
    transform 0.25s ease-out,
    box-shadow 0.15s ease-out;
  word-wrap: break-word;
  white-space: pre-wrap;
  word-break: break-word;
}

.user-menu a:after {
  content: '';
  position: absolute;
  top: 0.5em;
  right: 0.5em;
  height: 1.55em;
  background-repeat: no-repeat;
  width: 3em;
  background-position: top right;
}

.user-menu a .icon {
  display: none;
}

a.pending-review .icon {
  display: inline-block;
  width: 1em;
  height: auto;
  position: absolute;
  top: 2px;
  left: 2px;
}

.user-reviews :deep(.notification-badge) {
  box-shadow: 0 0 0 2px white;
  font-size: 1em;
  left: -0.25em;
  position: absolute;
  top: -0.25em;
}

.user-menu .menu-item-text {
  color: white;
  font-size: var(--body);
  font-weight: 600;
  line-height: 1;
  margin-top: auto;
  padding: 0 0.5em 0.9em;
  text-align: center;
  text-shadow: 0 0 32px hsla(0, 0%, 0%, 0.6);
}

.user-menu.en .menu-item-text {
  font-size: var(--title);
}

.user-menu a[disabled] {
  pointer-events: none;
}

.user-menu a[disabled] .menu-item-text {
  opacity: 0.5;
}

.user-menu a[disabled]:after {
  opacity: 0.5;
}

.user-menu a[disabled]:before {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  font-size: var(--body);
  line-height: 1;
  color: #333;
  text-align: center;
  padding: 1.5em 0.5em;
  z-index: 2;
}

.user-bookings {
  background-image:
    url('~/assets/user-account/bookings-tile.jpg'),
    linear-gradient(170deg, #88f0ff 20%, #003097);
}

.user-bookings:after {
  background: url('~/assets/user-account/bookings-icon.svg');
}

.user-favourites {
  background-image:
    url('~/assets/user-account/favourites-tile.jpg'),
    linear-gradient(170deg, #ffe2db 20%, #dd3e56, #990218);
}

.user-favourites:after {
  background: url('~/assets/user-account/favourites-icon.svg');
}

.user-messages {
  background-image:
    url('~/assets/user-account/messages-tile.jpg'),
    linear-gradient(170deg, #fffa46, #e83a04);
}

.user-messages:after {
  background: url('~/assets/user-account/messages-icon.svg');
}

.user-reviews {
  background-image:
    url('~/assets/user-account/reviews-tile.jpg'),
    linear-gradient(170deg, #e0b5e2 20%, #6f0073);
}

.user-reviews:after {
  background: url('~/assets/user-account/reviews-icon.svg');
}

.user-account {
  background-image:
    url('~/assets/user-account/account-tile.jpg'),
    linear-gradient(170deg, #aeb9c3 26%, #162c54);
}

.user-account:after {
  background: url('~/assets/user-account/account-icon.svg');
}

.user-settings {
  background-image:
    url('~/assets/user-account/settings-tile.jpg'),
    linear-gradient(170deg, #ced8e3 20%, #64707f);
}

.user-settings:after {
  background: url('~/assets/user-account/settings-icon.svg');
}

.manager-portal {
  background-color: var(--colour-dark-blue);
  border-radius: 0.3em;
  box-shadow: var(--card-box-shadow);
  color: white;
  text-decoration: none;
  user-select: none;
  min-height: 5em;
  display: block;
  padding: 1em;
  margin: 1.4em;
  transition:
    transform 0.25s ease-out,
    box-shadow 0.15s ease-out;
}

.manager-portal .menu-item-text {
  font-weight: 600;
  font-size: var(--title);
}

.manager-portal p {
  font-size: var(--body);
  margin-top: 0.5em;
}

.user-menu a:active,
.user-menu a:focus-visible,
.manager-portal:hover,
.manager-portal:focus-visible {
  transform: translateY(0.2em) scale(1.05);
  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.15),
    0 3px 6px rgba(0, 0, 0, 0.1),
    0 0 0 6px var(--colour-dark-blue);
  filter: brightness(0.75);
  outline: none;
}

/* desktop & tablet */
.user-menu.desktop a {
  min-width: 7em;
  min-height: 7em;
  border-radius: 0.5em;
}

.user-menu.desktop a:hover,
.user-menu.desktop a:focus-visible {
  box-shadow: 0 0 0 6px var(--colour-dark-blue);
  transform: scale(1.05);
  outline: none;
  z-index: 1;
}

.user-menu.desktop a[disabled]:before {
  top: 2em;
  left: auto;
  padding: 0;
  text-align: center;
  position: relative;
}

.user-menu.desktop a[disabled] .menu-item-text {
  padding-top: 0.5em;
}

.user-menu.desktop .menu-item-text,
.manager-portal.desktop {
  font-size: 1.2em;
}

.user-menu.desktop .user-reviews :deep(.notification-badge) {
  font-size: var(--large);
}

.manager-portal.desktop {
  margin: 0;
  border: 4px solid transparent;
  border-radius: 0.5em;
  transition:
    transform 0.25s ease-out,
    box-shadow 0.15s ease-out;
}

.manager-portal.desktop:hover,
.manager-portal.desktop:focus-visible {
  box-shadow: 0 0 0 6px var(--colour-orange);
  transform: scale(1.05);
  outline: none;
}

.user-menu.desktop {
  margin: 0 auto;
  max-width: 60em;
}
</style>
